import { useState, useEffect } from 'react';
import { authHeaders } from '../utils/auth';

const TotpSetup = () => {
  const [status, setStatus]     = useState(null); // null | 'enabled' | 'disabled'
  const [qrData, setQrData]     = useState(null);
  const [secret, setSecret]     = useState('');
  const [code, setCode]         = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    fetch('/api/auth/totp-status', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setStatus(d.enabled ? 'enabled' : 'disabled'))
      .catch(() => setStatus('disabled'));
  }, []);

  const startSetup = async () => {
    setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await fetch('/api/auth/setup-totp', { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setQrData(data.qrDataUrl);
      setSecret(data.secret);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch('/api/auth/confirm-totp', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setStatus('enabled');
      setQrData(null);
      setSecret('');
      setCode('');
      setSuccess('Двухфакторная аутентификация включена!');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const disableTotp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch('/api/auth/disable-totp', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ code: disableCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setStatus('disabled');
      setDisableCode('');
      setSuccess('Двухфакторная аутентификация отключена.');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (status === null) return <p>Загрузка...</p>;

  return (
    <div style={{ maxWidth: 480 }}>
      <h3 style={{ marginTop: 0 }}>Двухфакторная аутентификация (TOTP)</h3>

      {success && <p style={{ color: '#2e7d32', background: '#e8f5e9', padding: '10px 14px', borderRadius: 6 }}>{success}</p>}
      {error   && <p style={{ color: '#c62828', background: '#ffebee', padding: '10px 14px', borderRadius: 6 }}>{error}</p>}

      {status === 'enabled' && !qrData && (
        <div>
          <p style={{ color: '#2e7d32', fontWeight: 600 }}>✅ 2FA включена</p>
          <p style={{ color: '#666', fontSize: 14 }}>При входе потребуется код из Google Authenticator / Authy.</p>
          <hr style={{ margin: '20px 0', borderColor: '#eee' }} />
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Отключить 2FA:</p>
          <form onSubmit={disableTotp} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              type="text"
              inputMode="numeric"
              value={disableCode}
              onChange={e => { setDisableCode(e.target.value); setError(''); }}
              placeholder="Код из приложения"
              maxLength={6}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 16, width: 160 }}
            />
            <button type="submit" disabled={loading || disableCode.length < 6}
              style={{ padding: '8px 16px', background: '#c62828', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
              Отключить
            </button>
          </form>
        </div>
      )}

      {status === 'disabled' && !qrData && (
        <div>
          <p style={{ color: '#666' }}>2FA не настроена. После включения при каждом входе будет запрашиваться одноразовый код.</p>
          <p style={{ fontSize: 14, color: '#888' }}>Нужно приложение: <strong>Google Authenticator</strong>, <strong>Authy</strong> или любой TOTP-совместимый.</p>
          <button onClick={startSetup} disabled={loading}
            style={{ marginTop: 12, padding: '10px 20px', background: '#db782e', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 15 }}>
            {loading ? 'Генерируем...' : 'Включить 2FA'}
          </button>
        </div>
      )}

      {qrData && (
        <div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>1. Отсканируйте QR-код в приложении:</p>
          <img src={qrData} alt="TOTP QR" style={{ width: 200, height: 200, display: 'block', marginBottom: 16, border: '1px solid #eee', borderRadius: 8 }} />
          <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>
            Или введите ключ вручную:<br />
            <code style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, fontSize: 12, wordBreak: 'break-all' }}>{secret}</code>
          </p>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>2. Введите код из приложения для подтверждения:</p>
          <form onSubmit={confirmSetup} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={e => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
              placeholder="000000"
              maxLength={6}
              autoComplete="one-time-code"
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 20, letterSpacing: 4, width: 140, textAlign: 'center' }}
            />
            <button type="submit" disabled={loading || code.length !== 6}
              style={{ padding: '8px 20px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 15 }}>
              {loading ? '...' : 'Подтвердить'}
            </button>
          </form>
          <button onClick={() => { setQrData(null); setSecret(''); setCode(''); setError(''); }}
            style={{ marginTop: 12, background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: 13 }}>
            ← Отмена
          </button>
        </div>
      )}
    </div>
  );
};

export default TotpSetup;
